import { createTheme, Theme, ThemeOptions, alpha } from '@mui/material/styles';
import { tokens } from './tokens';

/**
 * Creates the PDI IaC Agent Material-UI theme.
 * Light mode: clean white/slate surfaces, PDI blue accents.
 * Dark mode: deep navy surfaces (not pure black), same blue accents.
 * WCAG 2.1 AA compliant.
 */
export const createAppTheme = (mode: 'light' | 'dark' = 'light'): Theme => {
  const isDark = mode === 'dark';

  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main:          tokens.colors.primary[500],
        light:         tokens.colors.primary[300],
        dark:          tokens.colors.primary[700],
        contrastText:  tokens.colors.text.contrast,
      },
      secondary: {
        main:          tokens.colors.secondary[500],
        light:         tokens.colors.secondary[300],
        dark:          tokens.colors.secondary[700],
        contrastText:  tokens.colors.text.contrast,
      },
      success: {
        main:          tokens.colors.success[500],
        light:         tokens.colors.success[300],
        dark:          tokens.colors.success[700],
        contrastText:  tokens.colors.text.contrast,
      },
      error: {
        main:          tokens.colors.error[500],
        light:         tokens.colors.error[300],
        dark:          tokens.colors.error[700],
        contrastText:  tokens.colors.text.contrast,
      },
      warning: {
        main:          tokens.colors.warning[500],
        light:         tokens.colors.warning[300],
        dark:          tokens.colors.warning[700],
        contrastText:  isDark ? tokens.colors.dark.text.contrast : '#1a1a1a',
      },
      info: {
        main:          tokens.colors.info[500],
        light:         tokens.colors.info[300],
        dark:          tokens.colors.info[700],
        contrastText:  tokens.colors.text.contrast,
      },
      background: {
        default: isDark ? tokens.colors.dark.background.default : tokens.colors.background.default,
        paper:   isDark ? tokens.colors.dark.background.paper   : tokens.colors.background.paper,
      },
      text: {
        primary:  isDark ? tokens.colors.dark.text.primary   : tokens.colors.text.primary,
        secondary: isDark ? tokens.colors.dark.text.secondary : tokens.colors.text.secondary,
        disabled:  isDark ? tokens.colors.dark.text.disabled  : tokens.colors.text.disabled,
      },
      grey:    tokens.colors.grey as any,
      divider: isDark ? 'rgba(241, 245, 249, 0.10)' : 'rgba(15, 23, 42, 0.08)',
    },

    typography: {
      fontFamily:        tokens.typography.fontFamily.primary,
      fontSize:          16,
      fontWeightLight:   tokens.typography.fontWeight.light,
      fontWeightRegular: tokens.typography.fontWeight.regular,
      fontWeightMedium:  tokens.typography.fontWeight.medium,
      fontWeightBold:    tokens.typography.fontWeight.bold,
      h1: {
        fontSize:   tokens.typography.fontSize.xxxl,
        fontWeight: tokens.typography.fontWeight.bold,
        lineHeight: tokens.typography.lineHeight.tight,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize:   tokens.typography.fontSize.xxl,
        fontWeight: tokens.typography.fontWeight.bold,
        lineHeight: tokens.typography.lineHeight.tight,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize:   tokens.typography.fontSize.xl,
        fontWeight: tokens.typography.fontWeight.semibold,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      h4: {
        fontSize:   tokens.typography.fontSize.lg,
        fontWeight: tokens.typography.fontWeight.semibold,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      h5: {
        fontSize:   tokens.typography.fontSize.md,
        fontWeight: tokens.typography.fontWeight.medium,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      h6: {
        fontSize:   tokens.typography.fontSize.base,
        fontWeight: tokens.typography.fontWeight.medium,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      body1: {
        fontSize:   tokens.typography.fontSize.base,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      body2: {
        fontSize:   tokens.typography.fontSize.sm,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      caption: {
        fontSize:   tokens.typography.fontSize.xs,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      button: {
        fontSize:      tokens.typography.fontSize.sm,
        fontWeight:    tokens.typography.fontWeight.medium,
        textTransform: 'none',
        letterSpacing: '0.01em',
      },
    },

    spacing: tokens.spacing.sm, // 8px base

    shape: {
      borderRadius: tokens.borderRadius.md,
    },

    shadows: [
      'none',
      tokens.shadows.xs,
      tokens.shadows.sm,
      tokens.shadows.md,
      tokens.shadows.md,
      tokens.shadows.md,
      tokens.shadows.lg,
      tokens.shadows.lg,
      tokens.shadows.lg,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows.xxl,
      tokens.shadows.xxl,
    ],

    transitions: {
      duration: {
        shortest:      150,
        shorter:       200,
        short:         250,
        standard:      300,
        complex:       375,
        enteringScreen: 225,
        leavingScreen:  195,
      },
      easing: {
        easeInOut: tokens.transitions.easing.easeInOut,
        easeOut:   tokens.transitions.easing.easeOut,
        easeIn:    tokens.transitions.easing.easeIn,
        sharp:     tokens.transitions.easing.sharp,
      },
    },

    zIndex: {
      mobileStepper: 1000,
      fab:           1050,
      speedDial:     1050,
      appBar:        tokens.zIndex.sticky,
      drawer:        tokens.zIndex.fixed,
      modal:         tokens.zIndex.modal,
      snackbar:      tokens.zIndex.modal,
      tooltip:       tokens.zIndex.tooltip,
    },

    components: {
      // ── AppBar ──────────────────────────────────────────────────────────────
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isDark ? tokens.colors.dark.background.paper : '#ffffff',
            color:           isDark ? tokens.colors.dark.text.primary      : tokens.colors.text.primary,
            borderBottom:    `1px solid ${isDark ? 'rgba(241,245,249,0.08)' : 'rgba(15,23,42,0.08)'}`,
            boxShadow:       isDark
              ? '0 1px 0 0 rgba(241,245,249,0.06)'
              : '0 1px 3px 0 rgba(15,23,42,0.06), 0 1px 2px -1px rgba(15,23,42,0.04)',
          },
        },
      },

      // ── Paper / Cards ────────────────────────────────────────────────────────
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isDark ? tokens.colors.dark.background.paper : '#ffffff',
          },
          elevation1: { boxShadow: tokens.shadows.sm },
          elevation2: { boxShadow: tokens.shadows.md },
          elevation3: { boxShadow: tokens.shadows.lg },
        },
      },

      // ── Button ───────────────────────────────────────────────────────────────
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight:    tokens.touchTarget.minimum,
            minWidth:     tokens.touchTarget.comfortable,
            borderRadius: tokens.borderRadius.md,
            transition:   tokens.transitions.normal,
            '&:focus-visible': {
              outline:       `${tokens.accessibility.focusRing.width}px ${tokens.accessibility.focusRing.style} ${tokens.accessibility.focusRing.color}`,
              outlineOffset: tokens.accessibility.focusRing.offset,
            },
          },
          containedPrimary: {
            background:   tokens.colors.primary[500],
            '&:hover': {
              background: tokens.colors.primary[600],
              boxShadow:  tokens.shadows.md,
            },
          },
          sizeSmall: {
            minHeight: tokens.touchTarget.minimum,
            padding:   `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
          },
          sizeMedium: {
            padding: `${tokens.spacing.sm}px ${tokens.spacing.lg}px`,
          },
          sizeLarge: {
            minHeight: tokens.touchTarget.comfortable,
            padding:   `${tokens.spacing.md}px ${tokens.spacing.xl}px`,
          },
        },
      },

      // ── IconButton ───────────────────────────────────────────────────────────
      MuiIconButton: {
        styleOverrides: {
          root: {
            minWidth:  tokens.touchTarget.minimum,
            minHeight: tokens.touchTarget.minimum,
            '&:focus-visible': {
              outline:       `${tokens.accessibility.focusRing.width}px ${tokens.accessibility.focusRing.style} ${tokens.accessibility.focusRing.color}`,
              outlineOffset: tokens.accessibility.focusRing.offset,
            },
          },
          sizeSmall: {
            minWidth:  tokens.touchTarget.minimum,
            minHeight: tokens.touchTarget.minimum,
          },
          sizeMedium: {
            minWidth:  tokens.touchTarget.minimum,
            minHeight: tokens.touchTarget.minimum,
          },
          sizeLarge: {
            minWidth:  tokens.touchTarget.comfortable,
            minHeight: tokens.touchTarget.comfortable,
          },
        },
      },

      // ── TextField ────────────────────────────────────────────────────────────
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: tokens.colors.primary[500],
              },
            },
          },
        },
      },

      // ── Chip ─────────────────────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.xl,
          },
        },
      },

      // ── Dialog ───────────────────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            boxShadow:       tokens.shadows.xxl,
            borderRadius:    tokens.borderRadius.lg,
          },
        },
      },

      // ── Tooltip ──────────────────────────────────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize:     tokens.typography.fontSize.xs,
            padding:      `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
            borderRadius: tokens.borderRadius.sm,
            backgroundColor: isDark ? tokens.colors.grey[700] : tokens.colors.grey[800],
          },
        },
      },

      // ── Alert ────────────────────────────────────────────────────────────────
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.md,
          },
        },
      },

      // ── LinearProgress ───────────────────────────────────────────────────────
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.full,
            backgroundColor: isDark
              ? alpha(tokens.colors.primary[500], 0.15)
              : alpha(tokens.colors.primary[500], 0.12),
          },
          bar: {
            borderRadius: tokens.borderRadius.full,
          },
        },
      },

      // ── Divider ──────────────────────────────────────────────────────────────
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark ? 'rgba(241,245,249,0.08)' : 'rgba(15,23,42,0.08)',
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

// Pre-configured exports
export const lightTheme = createAppTheme('light');
export const darkTheme  = createAppTheme('dark');
